import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
export default class PollState {
  constructor(poll, post) {
    _defineProperty(this, "poll", void 0);
    _defineProperty(this, "post", void 0);
    _defineProperty(this, "pendingSubmit", false);
    // JRH fork: an ordered array rather than upstream's Set. Array order IS
    // the ballot's rank (index 0 = 1st choice) on ranked polls; on ordinary
    // multi-vote polls the order is simply unused, so one data structure
    // serves both. The backend does the same - it always derives rank from
    // submission order, and only the `ranked` flag decides who reads it.
    _defineProperty(this, "pendingOptions", null);
    _defineProperty(this, "loadingOptions", false);
    _defineProperty(this, "useSubmitUI", void 0);
    _defineProperty(this, "showCheckMarks", void 0);
    _defineProperty(this, "canSeeVoteCount", void 0);
    // JRH fork: set by PostPoll. Invoked whenever the staged ballot changes
    // so CommentPost can invalidate its SubtreeRetainer - without it the
    // retainer prunes the redraw and clicks appear to do nothing.
    _defineProperty(this, "onStateChange", void 0);
    _defineProperty(this, "showVoters", () => {
      // Load all the votes only when opening the votes list
      app.modal.show(() => import(/* webpackChunkName: 'forum/components/ListVotersModal', webpackMode: 'lazy-once' */ '../components/ListVotersModal'), {
        poll: this.poll,
        post: this.post
      });
    });
    this.poll = poll;
    this.post = post;
    // JRH fork: ranked polls ALWAYS stage the ballot behind a Submit
    // button. Upstream only stages when !canChangeVote, which breaks
    // ranked voting - a ranking is built up click by click, so
    // auto-submitting each intermediate click saves half-finished ballots
    // and makes the rank badges flash before the view flips to results.
    this.useSubmitUI = (poll == null ? void 0 : poll.ranked()) || !(poll != null && poll.canChangeVote()) && (poll == null ? void 0 : poll.allowMultipleVotes());
    this.showCheckMarks = !app.session.user || !poll.hasEnded() && poll.canVote() && (!this.hasVoted() || poll.canChangeVote());
    this.canSeeVoteCount = typeof poll.voteCount() === 'number';
    this.init();
  }

  /**
   * used as en extendable entry point for init customizations
   */
  init() {}
  isShowResult() {
    return this.poll.hasEnded() || this.canSeeVoteCount && !!app.session.user && this.hasVoted();
  }
  hasVoted() {
    return this.poll.myVotes().length > 0;
  }
  overallVoteCount() {
    return this.poll.voteCount();
  }
  hasVotedFor(option) {
    return this.pendingOptions ? this.pendingOptions.includes(option.id()) : this.poll.myVotes().some(vote => vote.option() === option);
  }

  /**
   * JRH fork: 1-based selection order, for the numbered rank badge - null
   * when the option isn't selected. Uses the pending (unsaved) click order
   * when there is one, otherwise the saved vote's rank, mirroring the same
   * split hasVotedFor already makes.
   */
  rankOf(option) {
    var _vote$rank;
    if (this.pendingOptions) {
      const index = this.pendingOptions.indexOf(option.id());
      return index === -1 ? null : index + 1;
    }
    const vote = this.poll.myVotes().find(v => v.option() === option);
    return vote ? (_vote$rank = vote.rank()) != null ? _vote$rank : null : null;
  }
  getMaxVotes() {
    const poll = this.poll;
    let maxVotes = poll.allowMultipleVotes() ? poll.maxVotes() : 1;
    if (maxVotes === 0) maxVotes = poll.options().length;
    return maxVotes;
  }
  showButton() {
    return this.useSubmitUI && this.pendingSubmit;
  }
  changeVote(option, evt) {
    const target = evt.target;
    if (!app.session.user) {
      app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
      target.checked = false;
      return;
    }
    const optionIds = this.pendingOptions || this.poll.myVotes().map(v => v.option().id());
    const allowsMultiple = this.poll.allowMultipleVotes();
    const existingIndex = optionIds.indexOf(option.id());
    const isUnvoting = existingIndex !== -1;
    if (isUnvoting) {
      // Splicing a middle rank out shifts the rest down so ranks stay
      // contiguous (old #2 becomes #1) - falls out of array order for
      // free, no renumbering pass needed.
      optionIds.splice(existingIndex, 1);
    } else if (!allowsMultiple) {
      optionIds.length = 0;
      optionIds.push(option.id());
    } else if (optionIds.length < this.getMaxVotes()) {
      // A new pick becomes the next rank. Clicking past the cap is a
      // no-op rather than bumping the oldest pick - least surprising, and
      // it can never exceed what the backend validator would accept.
      optionIds.push(option.id());
    }
    this.pendingOptions = optionIds.length ? optionIds : null;
    this.pendingSubmit = !!this.pendingOptions;
    if (this.useSubmitUI) {
      var _this$onStateChange;
      (_this$onStateChange = this.onStateChange) == null || _this$onStateChange.call(this);
      m.redraw();
      return;
    }
    this.submit(optionIds, () => {
      this.pendingOptions = null;
      this.pendingSubmit = false;
    }, () => target.checked = isUnvoting);
  }
  hasSelectedOptions() {
    return this.pendingSubmit;
  }
  onsubmit() {
    return this.submit(this.pendingOptions, () => {
      var _this$onStateChange2;
      this.pendingOptions = null;
      this.pendingSubmit = false;
      (_this$onStateChange2 = this.onStateChange) == null || _this$onStateChange2.call(this);
    });
  }
  submit(optionIds, cb, onerror) {
    if (onerror === void 0) {
      onerror = null;
    }
    this.loadingOptions = true;
    m.redraw();
    return app.request({
      method: 'PATCH',
      url: "".concat(app.forum.attribute('apiUrl'), "/polls/").concat(this.poll.id(), "/votes"),
      body: {
        data: {
          // Order matters: sent as-is, and the backend turns each
          // index into that vote's rank (see MultipleVotesPollHandler).
          optionIds: [...optionIds]
        }
      }
    }).then(res => {
      app.store.pushPayload(res);
      cb == null || cb();
    }).catch(err => {
      var _onerror;
      (_onerror = onerror) == null || _onerror(err);
    }).finally(() => {
      this.loadingOptions = false;
      this.canSeeVoteCount = typeof this.poll.voteCount() === 'number';
      m.redraw();
    });
  }
}
flarum.reg.add('fof-polls', 'forum/states/PollState', PollState);flarum.reg.addChunkModule('540', '2644', 'fof-polls', 'forum/components/ListVotersModal');